import React, { useRef, useState } from "react";
import { broadcast, useChannel } from "../core/hooks/useChannel";
import { PresenterMessages, PresenterState } from "./types";
import { closest, closestIndex, getElementIndex, getElementKey, getOffsetTopBackTo } from "../core/utils/dom";
import { toInt } from "../core/utils/conversion";
import { ReportMessages } from "./Report";
import { DataMessages } from "../core/hooks/useData";
import { PageTitleChannel, PageTitleMessages } from "../layout/PageTitle";
import { Icon } from "../core/ui/icons";
import { go } from "../core/route/Route";
import { getPresenter } from "./Presenter";
import { hasRole } from "../core/hooks/useSession";

export function Outline(props: {
	presenterID: string,
}) {

	const presenterRef = React.useRef<PresenterState>();

	const [, refresh] = React.useState({});

	const focusedIndex = useRef({
		headingIndex: 0,
		subTitleIndex: -1,
	});

	useChannel(props.presenterID, React.useCallback((msg) => {
		if (msg.type === PresenterMessages.READY) {
			presenterRef.current = msg.state;
			refresh({});
		
		} else if (msg.type === PresenterMessages.STRUCTURE_CHANGE) {
			refresh({});

		} else if (msg.type === PresenterMessages.SCROLL) {
			// when the presenter container scrolls need to workout what is closest heading
			if (!presenterRef.current) return;
			const presenter = presenterRef.current;

			const elContainer = document.getElementById(presenter.channelID + "-container");

			// if the scrollTop wasn't supplied then look it up
			if (msg.scrollTop === undefined) {
				if (elContainer) {
					msg.scrollTop = elContainer.scrollTop;
				}
			}

			// find first one positioned beyond the scroll
			const yScroll = msg.scrollTop;

			for(let i=presenter.structureHeadings.length-1;i>=0;i--) {
				const h = presenter.structureHeadings[i];

				// check subtitles too
				let foundSubtitle = false;
				if (h.subTitles) {
					for(let j=h.subTitles.length-1;j>=0;j--) {
						const st = h.subTitles[j];
						const top = getOffsetTopBackTo(st.element, elContainer) - 20;
						if (st.element.parentElement.offsetHeight > 0 && yScroll >= top) {
							if (focusedIndex.current.headingIndex !== i || focusedIndex.current.subTitleIndex !== j) {
								focusedIndex.current.headingIndex = i;
								focusedIndex.current.subTitleIndex = j;
								refresh({});
							}
							foundSubtitle = true;
							break;
						}
					}
				}
				if (foundSubtitle) {
					break;
				}

				const top = getOffsetTopBackTo(h.element, elContainer) - 20;
				if (yScroll >= top) {
					if (focusedIndex.current.headingIndex !== i || focusedIndex.current.subTitleIndex !== -1) {
						focusedIndex.current.headingIndex = i;
						focusedIndex.current.subTitleIndex = -1;
						refresh({});
					}
					break;
				}
			}

		}

	}, []));

	const presenter = presenterRef.current;

	const els = [];
	const headings = presenter?.structureHeadings;
	let iHeading = 0;
	if (headings && headings.length > 0) {
		let countMajor = 1;

		for(let h of headings) {
			els.push(
				<div
					className={
						"heading-item padding-3 interactive flex horiz align-center "
						+ ((focusedIndex.current.headingIndex === iHeading) ? "focused" : "")
					}
					key={countMajor}
					data-index={iHeading}
				>
					<div className="count margin-right-5">{countMajor}</div>
					<div className="fill">{h.title}</div>
				</div>
			);

			if (h.subTitles) {
				let countMinor = 1;
				let subtitleIndex = 0;
				for (let st of h.subTitles) {
					const number = countMajor + "." + countMinor;
					els.push(
						<div
							className={
								"heading-subitem margin-left-10 padding-3 interactive flex horiz align-center font-small "
								+ ((subtitleIndex === focusedIndex.current.subTitleIndex && iHeading === focusedIndex.current.headingIndex) ? "focused" : "")
							}
							key={number}
							data-key={iHeading}
							data-index={subtitleIndex}
						>
							<div className="margin-right-5">{number}</div>
							<div>{st.title}</div>
						</div>
					);	
					countMinor++;
					subtitleIndex++;
				}
			}

			countMajor++;
			iHeading++;
		}
	}


	return (
		<div
			className="outline padding-10 block"
			onClick={(e) => {
				// main heading
				const index = closestIndex(e.target, ".heading-item");
				if (index >= 0) {
					const h = presenter?.structureHeadings![index];
					const container = document.getElementById(props.presenterID + "-container");
					if (container) {
						container.scrollTop = getOffsetTopBackTo(h?.element, container);
					}
					return;
				}

				// subheading
				let elSub = closest(e.target, ".heading-subitem");
				if (elSub) {
					const headingIndex = toInt(getElementKey(elSub));
					const subitemIndex = getElementIndex(elSub);
					if (headingIndex >= 0 && subitemIndex! >= 0) {
						const h = presenter?.structureHeadings![headingIndex]!;
						if (h.repeatingFieldChannelID) {
							broadcast(h.repeatingFieldChannelID, {
								type: ReportMessages.SET_FOCUSED_INDEX,
								index: subitemIndex,
							})
						}
					}
				}
			}
		}>
			<div>
				<div style={{alignItems:"center", flexDirection: "column", display:"flex", marginBottom:"40px"}}>
					<Icon type="logo" size="logo-med" />
				</div>

				<FormTitle presenterID={props.presenterID} />

				{
					!presenter?.readOnly &&
					<button className="secondary" onClick={() => {

						broadcast(presenter?.channelID!, {
							type: PresenterMessages.SAVE,
						});

					}}>Save</button>
				}
				<button
					className="flat"
					onClick={() => {
						go("/dashboard/");
					}}
				>Cancel</button>
			</div>

			<div className="margin-top-5 padding-top-5" style={{borderTop:"solid #ccc 1px"}}>
				{els}
			</div>
		</div>
	);
}

function FormTitle(props: {
	presenterID: string,
}) {

	useChannel(props.presenterID, React.useCallback((msg) => {
		if (msg.type === PresenterMessages.READY) {
			setTitle(msg.state.title);
		}
	}, []));


	useChannel(PageTitleChannel, React.useCallback((msg) => {
		if (msg.type === PageTitleMessages.SET_TITLE) {
			if (msg.title !== title) {
				setTitle(msg.title);
			}
		}
	}, []));

	const [title, setTitle] = useState("");

	if (!title) return null;

	return (
		<div
			className="outline"
			style={{
				marginBottom: "30px",
				textAlign:"center"
			}}
			onClick={async () => {
				if (!hasRole("admin")) return;

				const presenter = getPresenter(props.presenterID);
				if (!presenter) return;

				const json = JSON.stringify(presenter.root.value, null, 4);
				await navigator.clipboard.writeText(json);
			}}

		>{title}</div>
	);
}